import React from "react";
import { Button, Modal, Spinner } from "react-bootstrap";

export type StatusChangeKind = "disable" | "enable";
/**
 * Entity label used in the title/body. Known finance labels keep their bespoke
 * wording; any other label (e.g. "department") falls back to a generic message.
 */
export type StatusChangeEntityLabel = string;

/** Title-cased noun overrides where the label differs from a simple title-case. */
const TITLE_OVERRIDES: Record<string, string> = {
    category: "Category",
    rule: "Allocation Rule",
};

const titleCase = (s: string): string => s.replace(/\b\w/g, (c) => c.toUpperCase());

const nounFor = (entityLabel: string): string => TITLE_OVERRIDES[entityLabel] ?? titleCase(entityLabel);

interface StatusChangeConfirmModalProps {
    show: boolean;
    kind: StatusChangeKind;
    entityLabel: StatusChangeEntityLabel;
    entityName: string;
    onConfirm: () => void;
    onCancel: () => void;
    busy?: boolean;
    /** Optional custom body content; overrides the default messaging. */
    bodyText?: React.ReactNode;
}

const titleFor = (kind: StatusChangeKind, entityLabel: StatusChangeEntityLabel): string =>
    `${kind === "disable" ? "Disable" : "Enable"} ${nounFor(entityLabel)}`;

const bodyFor = (
    kind: StatusChangeKind,
    entityLabel: StatusChangeEntityLabel,
    entityName: string,
    bodyText?: React.ReactNode
): React.ReactNode => {
    if (bodyText) return bodyText;

    if (kind === "disable") {
        if (entityLabel === "category") {
            return (
                <>
                    Are you sure you want to disable <strong>{entityName}</strong>? It will no longer be available for new finance calculations.
                </>
            );
        }
        if (entityLabel === "rule") {
            return (
                <>
                    Are you sure you want to disable <strong>{entityName}</strong>? It will no longer be applied to new finance allocations.
                </>
            );
        }
        return (
            <>
                Are you sure you want to disable <strong>{entityName}</strong>? It will be hidden from new selections, though existing records are unaffected.
            </>
        );
    }

    if (entityLabel === "category") {
        return (
            <>
                Are you sure you want to enable <strong>{entityName}</strong>? It will become active and available for new finance calculations.
            </>
        );
    }
    if (entityLabel === "rule") {
        return (
            <>
                Are you sure you want to enable <strong>{entityName}</strong>? It will become active and applied to new finance allocations.
            </>
        );
    }
    return (
        <>
            Are you sure you want to enable <strong>{entityName}</strong>? It will become active and available for new selections.
        </>
    );
};

const StatusChangeConfirmModal: React.FC<StatusChangeConfirmModalProps> = ({
    show,
    kind,
    entityLabel,
    entityName,
    onConfirm,
    onCancel,
    busy = false,
    bodyText,
}) => (
    <Modal
        show={show}
        onHide={onCancel}
        centered
        backdrop="static"
        keyboard={!busy}
        data-testid={`status-change-confirm-modal-${kind}`}
    >
        <Modal.Header closeButton={!busy} data-testid="status-change-confirm-modal-header">
            <Modal.Title>{titleFor(kind, entityLabel)}</Modal.Title>
        </Modal.Header>

        <Modal.Body data-testid="status-change-confirm-modal-body">
            {bodyFor(kind, entityLabel, entityName, bodyText)}
        </Modal.Body>

        <Modal.Footer data-testid="status-change-confirm-modal-footer">
            <Button
                variant="outline-secondary"
                onClick={onCancel}
                disabled={busy}
                data-testid="status-change-confirm-cancel-btn"
            >
                Cancel
            </Button>
            <Button
                variant={kind === "disable" ? "danger" : "success"}
                onClick={onConfirm}
                disabled={busy}
                aria-busy={busy}
                data-testid="status-change-confirm-yes-btn"
            >
                {busy ? (
                    <>
                        <Spinner size="sm" animation="border" className="me-2" aria-hidden="true" />
                        {kind === "disable" ? "Disabling…" : "Enabling…"}
                    </>
                ) : (
                    kind === "disable" ? "Yes, disable" : "Yes, enable"
                )}
            </Button>
        </Modal.Footer>
    </Modal>
);

export default StatusChangeConfirmModal;
